import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Baby, Stethoscope, CalendarDays, ClipboardList, Syringe, Activity } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useApi, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge, statusLabel } from "@/components/StatusBadge";

const MONTHS = ["janv", "févr", "mars", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"];

const Kpi = ({ icon: Icon, label, value, hint, id, to }) => (
  <Link to={to} className="bg-white border border-stone-200 rounded-lg p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg block" data-testid={`kpi-${id}`}>
    <div className="flex items-center justify-between gap-2"><p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-stone-500 truncate">{label}</p><Icon className="h-4 w-4 shrink-0 text-[#C2410C]" /></div>
    <p className="font-heading text-4xl font-black tracking-tight text-stone-900 mt-4" data-testid={`kpi-${id}-value`}>{value}</p>
    {hint && <p className="text-xs text-stone-500 mt-2">{hint}</p>}
  </Link>
);

const Box = ({ title, children, testId, className = "", action }) => (
  <div className={`bg-white border border-stone-200 rounded-lg p-6 ${className}`} data-testid={testId}>
    <div className="flex items-center"><h3 className="font-heading text-lg font-bold text-stone-900">{title}</h3>{action && <div className="ml-auto">{action}</div>}</div>
    <div className="mt-4">{children}</div>
  </div>
);

export default function Dashboard() {
  const api = useApi();
  const [s, setS] = useState(null);
  useEffect(() => { api("get", "/stats").then(setS); }, [api]);
  if (!s) return <p className="text-sm text-stone-500" data-testid="dashboard-loading">Chargement…</p>;
  const c = s.counts;
  const total = Object.values(s.appointments_by_status).reduce((a, b) => a + b, 0) || 1;
  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <PageHeader title="Tableau de bord" subtitle="Activité de l'application Sehatic-Pedia, synchronisée en continu." />
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Kpi icon={Users} id="parents" label="Parents" value={c.parents} to="/utilisateurs" />
        <Kpi icon={Baby} id="patients" label="Enfants" value={c.patients} to="/enfants" />
        <Kpi icon={Stethoscope} id="doctors" label="Médecins" value={c.doctors} hint={`${c.doctors_pending} à valider`} to="/medecins" />
        <Kpi icon={CalendarDays} id="appointments" label="Rendez-vous" value={c.appointments} to="/rendez-vous" />
        <Kpi icon={ClipboardList} id="consultations" label="Consultations" value={c.consultations} to="/dossiers" />
        <Kpi icon={Syringe} id="vaccines" label="Vaccins en retard" value={c.vaccinations_overdue} hint={`${c.vaccinations_due_30d} prévus sous 30 j`} to="/dossiers" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Box title="Consultations par mois" testId="consultations-chart" className="lg:col-span-2">
          <div className="h-64"><ResponsiveContainer>
            <BarChart data={s.consultations_by_month.map((m) => ({ name: MONTHS[parseInt(m.month.slice(5), 10) - 1], value: m.count }))}>
              <CartesianGrid vertical={false} stroke="#E7E5E4" />
              <XAxis dataKey="name" tickLine={false} axisLine={false} fontSize={12} stroke="#78716C" />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={12} stroke="#78716C" width={28} />
              <Tooltip cursor={{ fill: "#F5F5F4" }} formatter={(v) => [v, "Consultations"]} />
              <Bar dataKey="value" fill="#C2410C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer></div>
        </Box>
        <Box title="Épidémies (30 jours)" testId="epi-summary" action={<Link to="/epidemies" className="text-sm text-[#C2410C]" data-testid="epi-summary-link">Voir</Link>}>
          <p className="text-sm text-stone-600"><b className="text-2xl font-heading text-stone-900">{s.epi.total_30d}</b> cas · {s.epi.last_7_days} cette semaine</p>
          <div className="mt-4 space-y-2">
            {s.epi.alerts.length === 0 && <p className="text-sm text-stone-500">Aucun seuil approché.</p>}
            {s.epi.alerts.map((a) => <div key={a.pathology} className="flex items-center text-sm gap-2"><span className="text-stone-800">{a.pathology}</span><span className="text-stone-500">{a.cases_7d}/{a.threshold}</span><span className="ml-auto"><StatusBadge value={a.level} /></span></div>)}
          </div>
        </Box>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Box title="Statut des rendez-vous" testId="status-panel">
          <div className="space-y-4">
            {Object.entries(s.appointments_by_status).map(([k, v]) => (
              <div key={k}><div className="flex justify-between text-sm"><span className="text-stone-600">{statusLabel(k)}</span><span className="font-semibold">{v}</span></div>
                <div className="h-1.5 bg-stone-100 rounded mt-1.5"><div className="h-1.5 bg-[#C2410C] rounded" style={{ width: `${(v / total) * 100}%` }} /></div></div>
            ))}
          </div>
        </Box>
        <Box title="Prochains rendez-vous" testId="upcoming-list" className="lg:col-span-2" action={<Link to="/rendez-vous" className="text-sm text-[#C2410C]" data-testid="upcoming-see-all">Tout voir</Link>}>
          <div className="divide-y divide-stone-100">
            {s.upcoming.length === 0 && <p className="text-sm text-stone-500 py-2">Aucun rendez-vous à venir.</p>}
            {s.upcoming.map((a) => (
              <div key={a.id} className="py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm" data-testid={`upcoming-${a.id}`}>
                <span className="font-medium w-44">{fmtDate(a.start_time?.slice(0, 16))}</span><span>{a.patient_name}</span><span className="text-stone-500">{a.doctor_name}</span>
                <span className="ml-auto"><StatusBadge value={a.status} /></span>
              </div>
            ))}
          </div>
        </Box>
      </div>
      <p className="text-xs text-stone-500 flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> Données issues de Sehatic-Pedia — <Link to="/synchronisation" className="text-[#C2410C]">état de la synchronisation</Link></p>
    </div>
  );
}
